import React, { useEffect, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { FileUpload } from 'primereact/fileupload';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { fetchVendors } from '../apis/vendors';
import { fetchCategories } from '../apis/home/category';

type ProductFormMode = 'create' | 'edit';

interface ProductFormDialogProps {
    visible: boolean;
    mode: ProductFormMode;
    product?: any;
    loading?: boolean;
    onHide: () => void;
    onSubmit: (product: Record<string, any>) => Promise<void> | void;
}

const emptyForm = {
    name: '',
    image: '',
    price: '',
    withGstprice: '',
    category: '',
    description: '',
    gst: '',
    hsnCode: '',
    isInstock: true,
    nutritionInfo: '',
    quantity: '',
    soldBy: '',
    status: 'pending',
    unit: '',
    vendorId: '',
};

const toFormValue = (value: any) => (value === undefined || value === null ? '' : String(value));

function buildSearchKeywords(productName: string) {
    const name = productName.trim().toLowerCase();

    if (!name) {
        return [];
    }

    const words = name.split(/\s+/);
    const keywords: string[] = [];

    for (const word of words) {
        for (let i = 1; i <= word.length; i++) {
            keywords.push(word.slice(0, i));
        }
    }

    keywords.push(name);

    return [...new Set(keywords)];
}

const ProductFormDialog: React.FC<ProductFormDialogProps> = ({
    visible,
    mode,
    product,
    loading = false,
    onHide,
    onSubmit,
}) => {
    const [form, setForm] = useState({ ...emptyForm });
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState('');
    const fileUploadRef = useRef<any>(null);
    const vendorsQuery = useQuery({
        queryKey: ['product-form', 'vendors'],
        queryFn: async () => {
            const vendors = await fetchVendors(0, 'verified', 1000);
            return Array.isArray(vendors) ? vendors : [];
        },
        enabled: visible,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    const categoriesQuery = useQuery({
        queryKey: ['product-form', 'categories'],
        queryFn: async () => {
            const categories = await fetchCategories();
            return Array.isArray(categories) ? categories : [];
        },
        enabled: visible,
        staleTime: 10 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (!visible) {
            return;
        }

        if (mode === 'edit' && product) {
            setForm({
                name: product.name || '',
                image: product.image || '',
                price: toFormValue(product.price),
                withGstprice: toFormValue(product.withGstprice),
                category: product.category || '',
                description: product.description || '',
                gst: toFormValue(product.gst),
                hsnCode: product.hsnCode || '',
                isInstock: Boolean(product.isInstock),
                nutritionInfo: product.nutritionInfo || '',
                quantity: toFormValue(product.quantity),
                soldBy: product.soldBy || '',
                status: product.status || 'pending',
                unit: product.unit || '',
                vendorId: product.vendorId || '',
            });
            setImageFile(null);
            setImagePreview(product.image || '');
            return;
        }

        setForm({ ...emptyForm });
        setImageFile(null);
        setImagePreview('');
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    }, [visible, mode, product]);

    const setField = (name: string, value: any) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const handleImageSelect = (event: any) => {
        const file = event.files?.[0];
        if (!file) {
            return;
        }

        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const clearImage = () => {
        setImageFile(null);
        setImagePreview('');
        setField('image', '');
        if (fileUploadRef.current) {
            fileUploadRef.current.clear();
        }
    };

    const vendorOptions = (vendorsQuery.data || []).map((vendor: any) => ({
        label: vendor.shopName || vendor.name || vendor.vendorId,
        value: vendor.shopName || vendor.name || vendor.vendorId,
        vendorId: vendor.vendorId,
    }));

    const categoryOptions = (categoriesQuery.data || []).map((category: any) => ({
        label: category.title || category.name || category.categoryId,
        value: category.title || category.name || category.categoryId,
    }));

    const unitOptions = [
        { label: 'grams', value: 'grams' },
        { label: 'ml', value: 'ml' },
        { label: 'kg', value: 'kg' },
        { label: 'liters', value: 'liters' },
        { label: 'pcs', value: 'pcs' },
        { label: 'pkt', value: 'pkt' },
    ];

    const handleVendorChange = (value: string) => {
        setField('soldBy', value);
        const selectedVendor = (vendorsQuery.data || []).find((vendor: any) => {
            const vendorLabel = vendor.shopName || vendor.name || vendor.vendorId;
            return vendorLabel === value;
        });

        if (selectedVendor?.vendorId) {
            setField('vendorId', selectedVendor.vendorId);
        }
    };

    const parseNumber = (value: string) => {
        if (value === '') return undefined;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    };

    const calculateWithGstPrice = () => {
        const price = parseNumber(form.price);
        const gst = parseNumber(form.gst);

        if (price === undefined || gst === undefined) {
            return '';
        }

        return (price + (price * gst) / 100).toFixed(2);
    };

    const buildPayload = () => {
        const searchKeywords = buildSearchKeywords(form.name);
        const payload: Record<string, any> = {
            name: form.name.trim(),
            category: form.category.trim(),
            description: form.description.trim(),
            hsnCode: form.hsnCode.trim(),
            nutritionInfo: form.nutritionInfo.trim(),
            soldBy: form.soldBy.trim(),
            unit: form.unit.trim(),
            vendorId: form.vendorId.trim(),
            price: parseNumber(form.price),
            withGstprice: parseNumber(calculateWithGstPrice()),
            gst: parseNumber(form.gst),
            quantity: parseNumber(form.quantity),
            isInstock: form.isInstock,
            searchKeywords: JSON.stringify(searchKeywords),
        };

        if (imageFile) {
            payload.image = imageFile;
        } else if (form.image.trim()) {
            payload.image = form.image.trim();
        }

        if (mode === 'create' && form.status) {
            payload.status = form.status;
        }

        Object.keys(payload).forEach((key) => {
            const value = payload[key];
            if (value === '' || value === undefined || value === null) {
                delete payload[key];
            }
        });

        return payload;
    };

    const validateForm = () => {
        const requiredFields: Array<[string, string]> = [
            ['name', 'Product name is required'],
            ['price', 'Price is required'],
            ['gst', 'GST is required'],
            ['category', 'Category is required'],
            ['soldBy', 'Sold by is required'],
            ['hsnCode', 'HSN code is required'],
            ['unit', 'Unit is required'],
            ['quantity', 'Quantity is required'],
            ['description', 'Description is required'],
            ['nutritionInfo', 'Nutrition info is required'],
        ];

        for (const [field, message] of requiredFields) {
            if (!String(form[field as keyof typeof form] ?? '').trim()) {
                return message;
            }
        }

        if (mode === 'create' && !imageFile) {
            return 'Product image is required';
        }

        if (!form.vendorId.trim()) {
            return 'Please select a vendor';
        }

        if (
            parseNumber(form.price) === undefined ||
            parseNumber(form.gst) === undefined ||
            parseNumber(form.quantity) === undefined
        ) {
            return 'Please enter valid numeric values for price, GST, and quantity';
        }

        return '';
    };

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            toast.error(validationError);
            return;
        }

        await onSubmit(buildPayload());
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" severity="secondary" outlined onClick={onHide} type="button" />
            <Button label={mode === 'create' ? 'Create Product' : 'Save Changes'} icon="pi pi-check" onClick={handleSubmit} loading={loading} type="button" />
        </div>
    );

    return (
        <Dialog
            header={mode === 'create' ? 'Add Product' : 'Edit Product Details'}
            visible={visible}
            onHide={onHide}
            modal
            maximizable
            style={{ width: '70vw', minWidth: '900px' }}
            footer={footer}
            className="p-fluid"
        >
            <div className="grid">
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Name</label>
                    <InputText required value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Product name" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Product Image</label>
                    <FileUpload
                        ref={fileUploadRef}
                        mode="basic"
                        name="image"
                        accept="image/*"
                        chooseLabel="Upload Image"
                        customUpload
                        auto
                        uploadHandler={handleImageSelect}
                    />
                    {imagePreview ? (
                        <div className="mt-3 flex align-items-center gap-3">
                            <img
                                src={imagePreview}
                                alt="Product preview"
                                style={{ width: '72px', height: '72px', objectFit: 'cover', borderRadius: '12px' }}
                            />
                            <Button label="Remove" severity="secondary" outlined type="button" onClick={clearImage} />
                        </div>
                    ) : (
                        <small className="text-600 block mt-2">
                            Upload a product image. For edits, the existing image stays in place until you replace it.
                        </small>
                    )}
                </div>
                <div className="col-12 md:col-4">
                    <label className="block mb-2 font-medium">Price</label>
                    <InputText required value={form.price} onChange={(e) => setField('price', e.target.value)} placeholder="90" />
                </div>
                <div className="col-12 md:col-4">
                    <label className="block mb-2 font-medium">GST %</label>
                    <InputText required value={form.gst} onChange={(e) => setField('gst', e.target.value)} placeholder="5" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Category</label>
                    <Dropdown
                        required
                        value={form.category}
                        options={categoryOptions}
                        onChange={(e) => setField('category', e.value)}
                        placeholder="Select category"
                        filter
                        showClear
                    />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Sold By</label>
                    <Dropdown
                        required
                        value={form.soldBy}
                        options={vendorOptions}
                        onChange={(e) => handleVendorChange(e.value)}
                        placeholder="Select vendor"
                        filter
                        showClear
                    />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">HSN Code</label>
                    <InputText required value={form.hsnCode} onChange={(e) => setField('hsnCode', e.target.value)} placeholder="200899" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Unit</label>
                    <Dropdown
                        required
                        value={form.unit}
                        options={unitOptions}
                        onChange={(e) => setField('unit', e.value)}
                        placeholder="Select unit"
                        showClear
                    />
                </div>
                <div className="col-12 md:col-4">
                    <label className="block mb-2 font-medium">Quantity</label>
                    <InputText required value={form.quantity} onChange={(e) => setField('quantity', e.target.value)} placeholder="120" />
                </div>
                <div className="col-12 md:col-4">
                    <label className="block mb-2 font-medium">Stock</label>
                    <div className="flex align-items-center gap-3">
                        <InputSwitch checked={form.isInstock} onChange={(e) => setField('isInstock', e.value)} />
                        <span>{form.isInstock ? 'In Stock' : 'Out of Stock'}</span>
                    </div>
                </div>
                {mode === 'create' && (
                    <div className="col-12 md:col-6">
                        <label className="block mb-2 font-medium">Status</label>
                        <Dropdown
                            value={form.status}
                            options={[
                                { label: 'Pending', value: 'pending' },
                                { label: 'Approved', value: 'approved' },
                                { label: 'Rejected', value: 'rejected' },
                            ]}
                            onChange={(e) => setField('status', e.value)}
                            placeholder="Select status"
                        />
                    </div>
                )}
                <div className="col-12">
                    <label className="block mb-2 font-medium">Description</label>
                    <InputTextarea required rows={3} value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder="Product description" />
                </div>
                <div className="col-12">
                    <label className="block mb-2 font-medium">Nutrition Info</label>
                    <InputTextarea required rows={3} value={form.nutritionInfo} onChange={(e) => setField('nutritionInfo', e.target.value)} placeholder="Calories, protein, etc." />
                </div>
                <div className="col-12 md:col-6">
                    <small className="text-600 block mt-2">
                        Vendor ID is assigned automatically from the selected vendor.
                    </small>
                </div>
            </div>
        </Dialog>
    );
};

export default ProductFormDialog;
